import { ArrayHelper } from '../../classes/array.helper';
import { IOption } from '../../interfaces/common/i-option';
import { RADIX, RadixColor } from '../radix-ui';

export enum MlbSportId {
  MLB = 1,
  AAA = 11,
  AA = 12,
  HighA = 13,
  SingleA = 14,
  Rookie = 16,
}

export enum RookieLeague {
  ACL = 'ACL',
  FCL = 'FCL',
  DSL = 'DSL',
}

export enum RosterType {
  FullRoster = 'fullRoster',
  FortyMan = '40Man',
  Active = 'active',
}

export enum PlayerPositionCode {
  BaseRunner = 'BR',
  Batter = '10', // same code as DesignatedHitter
  Catcher = '2',
  CenterField = '8',
  Closer = 'C',
  DesignatedHitter = '10',
  ExtraHitter = '13',
  FirstBase = '3',
  Infield = 'I',
  LeftField = '7', // fullName is "Outfielder" — disambiguated via shortName
  LeftHandedPitcher = 'L',
  LeftHandedReliever = 'G',
  LeftHandedStarter = 'N',
  Outfield = 'O',
  PinchHitter = '11',
  PinchRunner = '12',
  Pitcher = '1',
  PitcherInfielder = 'A',
  PitcherOutfielder = 'J',
  PitcherUtility = 'Z',
  ReliefPitcher = 'E',
  RightField = '9',
  RightHandedPitcher = 'K',
  RightHandedReliever = 'F',
  RightHandedStarter = 'M',
  RunnerOnFirst = 'R1',
  RunnerOnSecond = 'R2',
  RunnerOnThird = 'R3',
  SecondBase = '4',
  Shortstop = '6',
  StartingPitcher = 'S',
  ThirdBase = '5',
  TwoWayPlayer = 'Y',
  Unknown = 'X',
  Utility = 'U',
  UtilityInfielder = 'V',
  UtilityOutfielder = 'W',
}

export enum PlayerPositionAbbrev {
  BaseRunner = 'BR',
  Batter = 'B',
  Catcher = 'C',
  CenterField = 'CF',
  Closer = 'CP',
  DesignatedHitter = 'DH',
  ExtraHitter = 'EH',
  FirstBase = '1B',
  Infield = 'IF',
  LeftField = 'LF', // fullName is "Outfielder" — disambiguated via shortName
  LeftHandedPitcher = 'LHP',
  LeftHandedReliever = 'LHR',
  LeftHandedStarter = 'LHS',
  Outfield = 'OF',
  PinchHitter = 'PH',
  PinchRunner = 'PR',
  Pitcher = 'P',
  PitcherInfielder = 'P-IF',
  PitcherOutfielder = 'P-OF',
  PitcherUtility = 'P-UT',
  ReliefPitcher = 'RP',
  RightField = 'RF',
  RightHandedPitcher = 'RHP',
  RightHandedReliever = 'RHR',
  RightHandedStarter = 'RHS',
  RunnerOnFirst = 'R1',
  RunnerOnSecond = 'R2',
  RunnerOnThird = 'R3',
  SecondBase = '2B',
  Shortstop = 'SS',
  StartingPitcher = 'SP',
  ThirdBase = '3B',
  TwoWayPlayer = 'TWP',
  Unknown = 'X',
  Utility = 'UT',
  UtilityInfielder = 'UI',
  UtilityOutfielder = 'UO',
}

export enum PlayerStatusCode {
  Active = 'A',
  AdministrativeLeave = 'ADM',
  Assigned = 'ASG',
  Banned = 'BN',
  Bereavement = 'BRV',
  Claimed = 'CL',
  Deceased = 'DEC',
  DevelopmentList = 'DEV',
  DFA = 'DES',
  FamilyMedical = 'FME',
  FifteenDayIL = 'D15',
  FortyFiveDayIL = 'D45',
  FortyMan = '40M',
  FreeAgent = 'FA',
  FullSeasonIL = 'ILF',
  IL = 'D0',
  IneligibleList = 'IN',
  MilitaryLeave = 'MIL',
  MinorLeagueContract = 'MIN',
  NotYetReported = 'NYR',
  NRI = 'NRI',
  Paternity = 'PL',
  Reassigned = 'RM',
  Rehab = 'RA',
  Released = 'RL',
  Reserve = 'RES',
  Restricted = 'RST',
  Retired = 'RET',
  SevenDayIL = 'D7',
  SixtyDayIL = 'D60',
  Suspended = 'SU',
  TaxiSquad = 'TAX',
  TemporaryInactive = 'TI',
  TenDayIL = 'D10',
  Traded = 'TR',
  Waived = 'WA',
}

export const PLAYER_STATUS_OPTIONS: IOption[] = [
  { value: PlayerStatusCode.Active, label: 'Active' },
  {
    value: PlayerStatusCode.AdministrativeLeave,
    label: 'Administrative Leave',
  },
  { value: PlayerStatusCode.Assigned, label: 'Assigned' },
  { value: PlayerStatusCode.Banned, label: 'Banned' },
  { value: PlayerStatusCode.Bereavement, label: 'Bereavement' },
  { value: PlayerStatusCode.Claimed, label: 'Claimed' },
  { value: PlayerStatusCode.Deceased, label: 'Deceased' },
  {
    // official desc: Development List
    value: PlayerStatusCode.DevelopmentList,
    label: 'Development',
  },
  { value: PlayerStatusCode.DFA, label: 'DFA' },
  { value: PlayerStatusCode.FamilyMedical, label: 'Family Medical' },
  { value: PlayerStatusCode.FifteenDayIL, label: '15-Day IL' },
  { value: PlayerStatusCode.FortyFiveDayIL, label: '45-Day IL' },
  { value: PlayerStatusCode.FortyMan, label: 'Forty Man' },
  { value: PlayerStatusCode.FreeAgent, label: 'Free agent' },
  { value: PlayerStatusCode.FullSeasonIL, label: 'Full Season IL' },
  { value: PlayerStatusCode.IL, label: 'IL' },
  {
    // official desc: Ineligible List
    value: PlayerStatusCode.IneligibleList,
    label: 'Ineligible',
  },
  { value: PlayerStatusCode.MilitaryLeave, label: 'Military Leave' },
  {
    value: PlayerStatusCode.MinorLeagueContract,
    label: 'Minor League Contract',
  },
  { value: PlayerStatusCode.NotYetReported, label: 'Not Yet Reported' },
  { value: PlayerStatusCode.NRI, label: 'NRI' },
  { value: PlayerStatusCode.Paternity, label: 'Paternity' },
  { value: PlayerStatusCode.Reassigned, label: 'Reassigned' },
  { value: PlayerStatusCode.Rehab, label: 'Rehab' },
  { value: PlayerStatusCode.Released, label: 'Released' },
  { value: PlayerStatusCode.Reserve, label: 'Reserve' },
  { value: PlayerStatusCode.Restricted, label: 'Restricted' },
  { value: PlayerStatusCode.Retired, label: 'Retired' },
  { value: PlayerStatusCode.SevenDayIL, label: '7-Day IL' },
  { value: PlayerStatusCode.SixtyDayIL, label: '60-Day IL' },
  { value: PlayerStatusCode.Suspended, label: 'Suspended' },
  { value: PlayerStatusCode.TaxiSquad, label: 'Taxi Squad' },
  { value: PlayerStatusCode.TemporaryInactive, label: 'Temporary Inactive' },
  { value: PlayerStatusCode.TenDayIL, label: '10-Day IL' },
  { value: PlayerStatusCode.Traded, label: 'Traded' },
  { value: PlayerStatusCode.Waived, label: 'Waived' },
];

const PLAYER_STATUS_DICT = ArrayHelper.firstBy(PLAYER_STATUS_OPTIONS, 'value');

export const getPlayerStatusDisplay = (
  status: PlayerStatusCode | undefined
): string | undefined => {
  switch (status) {
    case PlayerStatusCode.Active:
    case PlayerStatusCode.FortyMan:
    case undefined: {
      return undefined;
    }

    default: {
      return PLAYER_STATUS_DICT[status]?.label;
    }
  }
};

export const getPlayerStatusColor = (
  status: PlayerStatusCode | undefined
): RadixColor | undefined => {
  switch (status) {
    case PlayerStatusCode.Active:
    case PlayerStatusCode.FortyMan:
    case undefined: {
      return undefined;
    }

    default: {
      return RADIX.COLOR.DANGER;
    }
  }
};
